import {
  Box,
  Button,
  Checkbox,
  Circle,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  Spinner,
  Flex,
  Icon,
  Collapse,
  IconButton,
} from '@chakra-ui/react';
import AppColors from '../../../constants/AppColors';
import { FONT_FAMILY } from '../../../constants/Theme';
import { WarningIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useState } from 'react';

const PermanentDeleteModal = ({
  isDeleteLoading,
  onDelete,
  isDeleteModalOpen,
  closeDeleteModal,
  name,
  isChecked,
  icState,
  confirmationText,
  setConfirmationText,
  deletionPreview,
}) => {
  const isConfirmationValid = confirmationText.trim() === name;
  const isLoading = !deletionPreview && isDeleteModalOpen;

  const [expandedSections, setExpandedSections] = useState({
    healthSystem: false,
    hospitals: false,
    hospitalDetails: {},
    departments: false,
    departmentDetails: {},
    providers: false,
    charts: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleHospital = (hospitalId) => {
    setExpandedSections((prev) => ({
      ...prev,
      hospitalDetails: {
        ...prev.hospitalDetails,
        [hospitalId]: !prev.hospitalDetails[hospitalId],
      },
    }));
  };

  const toggleDepartment = (deptId) => {
    setExpandedSections((prev) => ({
      ...prev,
      departmentDetails: {
        ...prev.departmentDetails,
        [deptId]: !prev.departmentDetails[deptId],
      },
    }));
  };

  return (
    <Modal
      isOpen={isDeleteModalOpen}
      size={'3xl'}
      onClose={() => closeDeleteModal()}
      isCentered
    >
      <ModalOverlay style={{ backgroundColor: 'rgba(0, 26, 65, 0.8)' }} />
      <ModalContent
        p={8}
        style={{ alignItems: 'center', maxWidth: '850px', width: '95%' }}
        borderRadius="xl"
        maxH="90vh"
        overflowY="auto"
      >
        <Box
          style={{ zIndex: '1' }}
          position={'absolute'}
          top={-14}
          right={-10}
        >
          <Circle>
            <ModalCloseButton
              size={'md'}
              style={{ color: AppColors.white }}
              backgroundColor={'#8894A6'}
              p={3}
              onClick={closeDeleteModal}
              borderRadius={50}
              _hover={{ backgroundColor: '#6b7785' }}
            />
          </Circle>
        </Box>

        <ModalHeader width="100%" px={0} pt={0}>
          {/* Warning Icon */}
          <Flex justify="center" mb={4}>
            <Box bg="red.50" borderRadius="full" p={4}>
              <Icon as={WarningIcon} w={10} h={10} color={AppColors.red} />
            </Box>
          </Flex>

          {/* Title */}
          <Text
            textAlign={'center'}
            fontFamily={FONT_FAMILY.baiJamurjee}
            fontWeight="bold"
            fontSize={'2xl'}
            mb={2}
            color={AppColors.black}
          >
            Permanent Deletion
          </Text>

          {/* Subtitle */}
          <Text
            textAlign={'center'}
            fontFamily={FONT_FAMILY.ptSans}
            fontSize={'md'}
            mb={6}
            color={AppColors.black}
            px={4}
          >
            You are about to permanently delete{' '}
            <Text as="span" fontWeight="bold" color={AppColors.red}>
              "{name}"
            </Text>
          </Text>

          {/* Loading State */}
          {isLoading && (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py={8}
              gap={4}
            >
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color={AppColors.secondary}
                size="xl"
              />
              <Text color={AppColors.black} fontSize="sm">
                Calculating what will be deleted...
              </Text>
            </Flex>
          )}

          {/* Content - Only show when loaded */}
          {!isLoading && deletionPreview && (
            <>
              {/* Warning Message */}
              <Box
                bg="red.50"
                borderRadius="lg"
                p={4}
                mb={6}
                borderLeft="4px solid"
                borderColor={AppColors.red}
                maxH="500px"
                overflowY="auto"
              >
                <Text fontSize="sm" color="red.800" fontWeight="medium" mb={3}>
                  ⚠️ This action will permanently delete:
                </Text>
                <VStack align="stretch" spacing={3} pl={2}>
                  
                  {/* Health System with Users - EXPANDABLE */}
                  <Box>
                    <Flex align="center" justify="space-between">
                      <Text fontSize="sm" color="red.700" fontWeight="bold">
                        • Health System: {deletionPreview.health_system?.name}
                      </Text>
                      <IconButton
                        icon={expandedSections.healthSystem ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        size="xs"
                        variant="ghost"
                        onClick={() => toggleSection('healthSystem')}
                        aria-label="Toggle health system"
                        colorScheme="red"
                      />
                    </Flex>
                    <Collapse in={expandedSections.healthSystem}>
                      <VStack align="stretch" spacing={1} mt={2} pl={4}>
                        {deletionPreview.health_system_users && deletionPreview.health_system_users.length > 0 ? (
                          <>
                            <Text fontSize="xs" color="red.600" fontWeight="semibold">
                              Users attached to Health System:
                            </Text>
                            {deletionPreview.health_system_users.map((user, idx) => (
                              <Text key={idx} fontSize="xs" color="red.600" pl={2}>
                                → {user.user__first_name} {user.user__last_name} ({user.user__email ? user.user__email : 'no email'})
                              </Text>
                            ))}
                          </>
                        ) : (
                          <Text fontSize="xs" color="red.500" fontStyle="italic">
                            No users directly attached to this Health System
                          </Text>
                        )}
                      </VStack>
                    </Collapse>
                  </Box>

                  {/* Hospitals - EXPANDABLE */}
                  {deletionPreview.hospitals?.count > 0 && (
                    <Box>
                      <Flex align="center" justify="space-between">
                        <Text fontSize="sm" color="red.700" fontWeight="semibold">
                          • {deletionPreview.hospitals.count}{' '}
                          {deletionPreview.hospitals.count === 1 ? 'Hospital' : 'Hospitals'}
                        </Text>
                        <IconButton
                          icon={expandedSections.hospitals ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => toggleSection('hospitals')}
                          aria-label="Toggle hospitals"
                          colorScheme="red"
                        />
                      </Flex>
                      <Collapse in={expandedSections.hospitals}>
                        <VStack align="stretch" spacing={2} mt={2} pl={4}>
                          {deletionPreview.hospitals.details?.map((hospital) => (
                            <Box key={hospital.id}>
                              <Flex align="center" justify="space-between">
                                <Text fontSize="xs" color="red.600" fontWeight="semibold">
                                  → {hospital.name}
                                </Text>
                                {hospital.users && hospital.users.length > 0 && (
                                  <IconButton
                                    icon={
                                      expandedSections.hospitalDetails[hospital.id] ? (
                                        <ChevronUpIcon />
                                      ) : (
                                        <ChevronDownIcon />
                                      )
                                    }
                                    size="xs"
                                    variant="ghost"
                                    onClick={() => toggleHospital(hospital.id)}
                                    aria-label="Toggle hospital users"
                                    colorScheme="red"
                                  />
                                )}
                              </Flex>
                              <Collapse in={expandedSections.hospitalDetails[hospital.id]}>
                                {hospital.users && hospital.users.length > 0 ? (
                                  <VStack align="stretch" spacing={1} mt={1} pl={4}>
                                    <Text fontSize="xs" color="red.500" fontStyle="italic">
                                      Users:
                                    </Text>
                                    {hospital.users.map((user, idx) => (
                                      <Text key={idx} fontSize="xs" color="red.500" pl={2}>
                                        • {user.user__first_name} {user.user__last_name} ({user.user__email ? user.user__email : 'no email'})
                                      </Text>
                                    ))}
                                  </VStack>
                                ) : (
                                  <Text fontSize="xs" color="red.500" fontStyle="italic" pl={4}>
                                    No users attached to this hospital
                                  </Text>
                                )}
                              </Collapse>
                            </Box>
                          ))}
                        </VStack>
                      </Collapse>
                    </Box>
                  )}

                  {/* Departments - EXPANDABLE */}
                  {deletionPreview.departments?.count > 0 && (
                    <Box>
                      <Flex align="center" justify="space-between">
                        <Text fontSize="sm" color="red.700" fontWeight="semibold">
                          • {deletionPreview.departments.count}{' '}
                          {deletionPreview.departments.count === 1 ? 'Department' : 'Departments'}
                        </Text>
                        <IconButton
                          icon={expandedSections.departments ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => toggleSection('departments')}
                          aria-label="Toggle departments"
                          colorScheme="red"
                        />
                      </Flex>
                      <Collapse in={expandedSections.departments}>
                        <VStack align="stretch" spacing={2} mt={2} pl={4} maxH="250px" overflowY="auto">
                          {deletionPreview.departments.details?.map((dept) => (
                            <Box key={dept.id}>
                              <Flex align="center" justify="space-between">
                                <Text fontSize="xs" color="red.600" fontWeight="semibold">
                                  → {dept.specialty__name || 'N/A'} at {dept.hospital__name || 'N/A'}
                                </Text>
                                {dept.users && dept.users.length > 0 && (
                                  <IconButton
                                    icon={
                                      expandedSections.departmentDetails[dept.id] ? (
                                        <ChevronUpIcon />
                                      ) : (
                                        <ChevronDownIcon />
                                      )
                                    }
                                    size="xs"
                                    variant="ghost"
                                    onClick={() => toggleDepartment(dept.id)}
                                    aria-label="Toggle department users"
                                    colorScheme="red"
                                  />
                                )}
                              </Flex>
                              <Collapse in={expandedSections.departmentDetails[dept.id]}>
                                {dept.users && dept.users.length > 0 ? (
                                  <VStack align="stretch" spacing={1} mt={1} pl={4}>
                                    <Text fontSize="xs" color="red.500" fontStyle="italic">
                                      Users:
                                    </Text>
                                    {dept.users.map((user, idx) => (
                                      <Text key={idx} fontSize="xs" color="red.500" pl={2}>
                                        • {user.user__first_name} {user.user__last_name} ({user.user__email ? user.user__email : 'no email'})
                                      </Text>
                                    ))}
                                  </VStack>
                                ) : (
                                  <Text fontSize="xs" color="red.500" fontStyle="italic" pl={4}>
                                    No users attached to this department
                                  </Text>
                                )}
                              </Collapse>
                            </Box>
                          ))}
                        </VStack>
                      </Collapse>
                    </Box>
                  )}

                  {/* Providers - EXPANDABLE */}
                  {deletionPreview.providers?.count > 0 && (
                    <Box>
                      <Flex align="center" justify="space-between">
                        <Text fontSize="sm" color="red.700" fontWeight="semibold">
                          • {deletionPreview.providers.count}{' '}
                          {deletionPreview.providers.count === 1 ? 'Provider' : 'Providers'}{' '}
                          (All providers and their accounts will be deleted)
                        </Text>
                        <IconButton
                          icon={expandedSections.providers ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => toggleSection('providers')}
                          aria-label="Toggle providers"
                          colorScheme="red"
                        />
                      </Flex>
                      <Collapse in={expandedSections.providers}>
                        <VStack align="stretch" spacing={1} mt={2} pl={4} maxH="200px" overflowY="auto">
                          {deletionPreview.providers.details?.map((provider, idx) => (
                            <Text key={idx} fontSize="xs" color="red.600">
                              → {provider.user__first_name} {provider.user__last_name} ({provider.user__email ? provider.user__email : 'no email'})
                            </Text>
                          ))}
                        </VStack>
                      </Collapse>
                    </Box>
                  )}

                  {/* Charts - EXPANDABLE */}
                  {deletionPreview.charts?.count > 0 && (
                    <Box>
                      <Flex align="center" justify="space-between">
                        <Text fontSize="sm" color="red.700" fontWeight="semibold">
                          • {deletionPreview.charts.count}{' '}
                          {deletionPreview.charts.count === 1 ? 'Chart' : 'Charts'}{' '}
                          (including all audit data)
                        </Text>
                        <IconButton
                          icon={expandedSections.charts ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => toggleSection('charts')}
                          aria-label="Toggle charts"
                          colorScheme="red"
                        />
                      </Flex>
                      <Collapse in={expandedSections.charts}>
                        <VStack align="stretch" spacing={1} mt={2} pl={4} maxH="200px" overflowY="auto">
                          {deletionPreview.charts.details?.map((chart, idx) => (
                            <Text key={idx} fontSize="xs" color="red.600">
                              → Chart ID: {chart.chart_id} - {chart.client__user__first_name} {chart.client__user__last_name} ({chart.status})
                            </Text>
                          ))}
                        </VStack>
                      </Collapse>
                    </Box>
                  )}
                </VStack>
              </Box>

              {/* Confirmation Input */}
              <Box mb={4}>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  mb={2}
                  color={AppColors.black}
                >
                  Type{' '}
                  <Text
                    as="span"
                    fontFamily="mono"
                    bg="gray.100"
                    px={2}
                    py={1}
                    borderRadius="md"
                    color={AppColors.red}
                    fontSize="sm"
                  >
                    {name}
                  </Text>{' '}
                  to confirm:
                </Text>
                <Input
                  placeholder={`Type "${name}" here`}
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  borderColor={
                    confirmationText
                      ? isConfirmationValid
                        ? 'green.400'
                        : 'red.400'
                      : 'gray.300'
                  }
                  focusBorderColor={AppColors.secondary}
                  size="lg"
                  fontSize="md"
                  _placeholder={{ color: 'gray.400' }}
                />
              </Box>

              {/* Checkbox Confirmation */}
              <Box mb={4}>
                <Checkbox
                  size="md"
                  colorScheme="red"
                  onChange={() => icState.toggle()}
                  isChecked={isChecked}
                  alignItems="flex-start"
                >
                  <Text fontSize="sm" color={AppColors.black} lineHeight="1.5">
                    I understand this action is{' '}
                    <Text as="span" fontWeight="bold" color={AppColors.red}>
                      PERMANENT
                    </Text>{' '}
                    and cannot be undone
                  </Text>
                </Checkbox>
              </Box>

              {/* Info Text */}
              <Text
                fontSize="xs"
                color={AppColors.black}
                textAlign="center"
                fontStyle="italic"
              >
                All data will be permanently removed from the database
              </Text>
            </>
          )}
        </ModalHeader>

        {/* Action Buttons - Only show when loaded */}
        {!isLoading && (
          <ModalBody mb={2} px={0} w="100%">
            <HStack justify="center" spacing={3} w="100%">
              <Button
                color={AppColors.black}
                bg="white"
                borderColor="gray.300"
                borderWidth={2}
                fontSize="md"
                fontWeight="semibold"
                borderRadius="full"
                height="50px"
                onClick={() => closeDeleteModal()}
                _hover={{
                  bg: 'gray.50',
                  borderColor: 'gray.400',
                }}
                px={12}
                flex={1}
              >
                Cancel
              </Button>
              <Button
                borderRadius="full"
                color="white"
                fontSize="md"
                fontWeight="bold"
                height="50px"
                bg={AppColors.red}
                px={12}
                flex={1}
                isLoading={isDeleteLoading}
                isDisabled={!isChecked || !isConfirmationValid}
                _hover={{
                  bg: 'red.600',
                  transform: 'translateY(-1px)',
                  boxShadow: 'lg',
                }}
                _active={{
                  transform: 'translateY(0)',
                }}
                _disabled={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  _hover: {
                    bg: AppColors.red,
                    transform: 'none',
                    boxShadow: 'none',
                  },
                }}
                onClick={onDelete}
              >
                Delete Forever
              </Button>
            </HStack>
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  );
};

export { PermanentDeleteModal };